import React, { useState, useEffect, useContext } from 'react';
import { decimalFormatter } from 'op-utils';
import { useRouteMatch } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client';
import {
  GET_CAREPLAN_PRESCRIPTIONS,
  UPDATE_SITE_VALUE,
  GET_CAREPLAN,
  SITE_TEMPLATE_VALUES_IN_PRESCRIPTION,
  SITE_TEMPLATE_VALUES_BY_PRESCRIPTION_VARIATION,
} from '../Queries';
import {
  ROPatientCarePlanRoute,
  ServerPrescriptionType,
  PrescriptionSiteGroupType,
  PrescriptionSiteType,
  ValueType,
} from '../Interface';
import './VolumingPage.scss';
import { CleanSpecifyFromSiteName, getPrescriptionsWithSites, isSpecifySite, removeUnicodeQuotes } from '../Common';
import TargetValueField from './TargetValueField';
import SiteTargetValuesSkeleton from './SiteTargetValuesSkeleton';
import { Typography, Stack, useTheme, Divider } from '@mui/material';
import { TenantContext } from 'op-contexts';
import { DoseUnit, GY_TO_CGY } from 'op-enums';

interface TreatmentRuleSite {
  id: number;
  field: {
    id: number;
    name: string;
  };
  defaultValue: string;
}

interface TemplateSet {
  id: number;
  treatmentrulesiteSet: TreatmentRuleSite[];
}

interface SiteGroupTemplate {
  id: number;
  treatmentsiteprescriptiontemplateSet: TemplateSet[];
}

interface PrescriptionTemplate {
  sitegrouptemplateSet: SiteGroupTemplate[];
}

interface SiteTemplate {
  prescriptionTemplate: PrescriptionTemplate;
}

interface Props {
  cpotTriggered: boolean;
  refresh: boolean;
  targetValueDoseSuffixEnabled: boolean;
}

interface SiteTargetValuesState {
  name: string;
  id: number;
  templateSiteId: number;
  GTVId: number;
  CTVId: number;
  PTVId: number;
  GTV: string;
  CTV: string[];
  PTV: string[];
  Dose: number[];
  PTVOffTemplate: boolean;
  CTVOffTemplate: boolean;
  GTVOffTemplate: boolean;
  [key: string]: any;
}

interface SiteTemplateValues {
  GTV: string;
  CTV: string[];
  PTV: string[];
}

interface SiteTemplateState {
  [key: number]: SiteTemplateValues;
}

interface ServerPrescriptionQuery {
  prescriptionsByCareplan: ServerPrescriptionType[];
}

const SiteTargetValues = (props: Props): JSX.Element => {
  const theme = useTheme();
  const match = useRouteMatch<ROPatientCarePlanRoute>();
  const { careplanId, prescriptionIdx } = match.params;
  const [state, setState] = useState<SiteTargetValuesState[]>([]);
  const [templateState, setTemplateState] = useState<SiteTemplateState>();
  const [prescriptionId, setPrescriptionId] = useState(-1);
  const { doseUnit } = useContext(TenantContext);
  const targetValueDoseSuffixEnabled = props?.targetValueDoseSuffixEnabled;

  const getCurrentPrescriptionIndex = (prescriptionData: ServerPrescriptionQuery): number => {
    if (!prescriptionData) return 0;
    let currentPrescriptionIndex: number = Number(prescriptionIdx || 0);
    if (prescriptionIdx === 'last') {
      currentPrescriptionIndex = getPrescriptionsWithSites(prescriptionData?.prescriptionsByCareplan).length - 1;
    }
    return currentPrescriptionIndex;
  };

  const cpotTriggered = props?.cpotTriggered;
  const { loading, data, refetch } = useQuery(GET_CAREPLAN_PRESCRIPTIONS, {
    variables: { careplanId },
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    onCompleted: (data: ServerPrescriptionQuery): void => {
      setVolumingTValues(data);
    },
  });

  const [updateSiteVoluming] = useMutation(UPDATE_SITE_VALUE, {
    refetchQueries: [
      {
        query: SITE_TEMPLATE_VALUES_BY_PRESCRIPTION_VARIATION,
        variables: {
          prescriptionId:
            getPrescriptionsWithSites(data?.prescriptionsByCareplan)[getCurrentPrescriptionIndex(data)]?.id || '',
          volumingPage: true,
        },
      },
      { query: GET_CAREPLAN, variables: { id: careplanId } },
    ],
  });

  const {
    data: siteTemplateData,
    refetch: refetchTemplateValues,
    loading: siteTemplateLoading,
  } = useQuery(SITE_TEMPLATE_VALUES_IN_PRESCRIPTION, {
    variables: { prescriptionId },
    skip: prescriptionId === -1,
    fetchPolicy: 'network-only',
    onCompleted: (data: SiteTemplate): void => {
      if (!data?.prescriptionTemplate) return;

      const sites = data.prescriptionTemplate.sitegrouptemplateSet
        .map((siteGroup: SiteGroupTemplate): TemplateSet[] => siteGroup.treatmentsiteprescriptiontemplateSet)
        .flat();
      const newTemplateTargetValues = sites.reduce((templateSite: any, site: TemplateSet): SiteTemplateState => {
        templateSite[site.id] = site.treatmentrulesiteSet.reduce(
          (volRules: any, rule: TreatmentRuleSite): SiteTemplateValues => {
            if (['CTV', 'GTV', 'PTV'].includes(rule.field.name)) {
              volRules[rule.field.name] = JSON.parse(rule.defaultValue);
            }
            return volRules;
          },
          {
            GTV: '',
            PTV: [],
            CTV: [],
          },
        );
        return templateSite;
      }, {});
      setTemplateState(newTemplateTargetValues);
    },
  });

  const setVolumingTValues = (data: ServerPrescriptionQuery): void => {
    if (data) {
      const prescriptionsWithSites = getPrescriptionsWithSites(data.prescriptionsByCareplan);
      const prescription = prescriptionsWithSites[getCurrentPrescriptionIndex(data) || 0];
      setPrescriptionId(Number(prescription?.id));
      const siteSet = prescription.sitegroupSet
        .map((siteGroup: PrescriptionSiteGroupType): PrescriptionSiteType[] => siteGroup.siteSet)
        .flat()
        .sort((a: PrescriptionSiteType, b: PrescriptionSiteType): number => a.orderRanking - b.orderRanking);
      const siteList = siteSet.map((site: PrescriptionSiteType): SiteTargetValuesState => {
        const location = site.sitevaluesSet?.reduce(
          (loc: string, value: ValueType): string => (value.field.name === 'Location' ? JSON.parse(value.value) : loc),
          '',
        );

        // Create initial state object
        const initialState: SiteTargetValuesState = {
          name: isSpecifySite(site.treatmentSite.treatmentSite)
            ? CleanSpecifyFromSiteName(`${site.treatmentSite.treatmentSite} (${location})`)
            : site.treatmentSite.treatmentSite,
          id: Number(site.id),
          templateSiteId: Number(site.template?.id),
          Dose: [],
          GTVId: -1,
          CTVId: -1,
          PTVId: -1,
          GTV: '',
          CTV: [],
          PTV: [],
          PTVOffTemplate: false,
          CTVOffTemplate: false,
          GTVOffTemplate: false,
        };

        // Process each value with proper type handling
        return site.sitevaluesSet?.reduce((siteAttr: any, sitevalue: ValueType): any => {
          const fieldName = sitevalue.field.name;
          if (['GTV', 'PTV', 'CTV'].includes(fieldName)) {
            try {
              siteAttr[fieldName] = JSON.parse(sitevalue.value);
            } catch (e) {
              // If JSON parsing fails, initialize with appropriate default value
              siteAttr[fieldName] = fieldName === 'GTV' ? '' : [];
            }
            siteAttr[`${fieldName}Id`] = sitevalue.id;
          }
          if (fieldName === 'Dose') {
            try {
              siteAttr[fieldName] = JSON.parse(sitevalue.value);
            } catch (e) {
              siteAttr[fieldName] = [];
            }
          }
          return siteAttr;
        }, initialState);
      });
      setState(siteList);
    }
  };

  // Trigger a refetch of data if the siteGroupIdx changes (switch to new voluming page)
  useEffect(() => {
    refetch();
  }, [prescriptionIdx, props.refresh]);

  useEffect(() => {
    if (data) {
      setVolumingTValues(data);
    }
  }, [prescriptionIdx, props.refresh]);

  if (loading) return <SiteTargetValuesSkeleton />;
  if (!data) return <div className="loading-error-page-settings">Error loading data...</div>;
  const doseNames = [
    [''],
    ['High Dose', 'Low Dose'],
    ['High Dose', 'Intermediate Dose', 'Low Dose'],
    ['High Dose', 'Intermediate 1 Dose', 'Intermediate 2 Dose', 'Low Dose'],
  ];
  return (
    <div>
      {state.map((site: SiteTargetValuesState, siteIndex: number): JSX.Element => {
        const maxTvLength = site.Dose.length;
        return (
          <div key={`fragment-${siteIndex}`}>
            <Divider />
            <Typography paddingTop={3} paddingBottom={1} variant="h6">
              {site.name}
            </Typography>
            <TargetValueField
              index={0}
              prefix="G"
              suffix=""
              initialValue={site?.GTV || ''}
              templateValue={templateState?.[site.templateSiteId]?.GTV || ''}
              cpotTriggered={cpotTriggered}
              targetValueDoseSuffixEnabled={targetValueDoseSuffixEnabled}
              updateField={(value: string) => {
                const newState = [...state];
                newState[siteIndex].GTV = value;
                setState(newState);
                updateSiteVoluming({
                  variables: {
                    id: site.GTVId,
                    siteId: site.id,
                    fieldName: 'GTV',
                    value: JSON.stringify(removeUnicodeQuotes(value)),
                  },
                });
              }}
            />
            {Array.isArray(site.Dose) &&
              site.Dose.map((dose: number, tvIndex: number): JSX.Element => {
                const templateCtv = templateState?.[site.templateSiteId]?.CTV?.[tvIndex] || '';
                const templatePtv = templateState?.[site.templateSiteId]?.PTV?.[tvIndex] || '';
                const doseValue = decimalFormatter(doseUnit === DoseUnit.GY ? dose * GY_TO_CGY : dose);
                const label = doseNames[maxTvLength - 1]?.[tvIndex] || '';
                return (
                  <Stack key={tvIndex}>
                    {!targetValueDoseSuffixEnabled && label && (
                      <Typography variant="subtitle1" color={theme.palette.text.secondary}>
                        {label}
                      </Typography>
                    )}
                    <TargetValueField
                      index={tvIndex}
                      prefix="C"
                      suffix={doseValue}
                      initialValue={Array.isArray(site.CTV) ? site.CTV[tvIndex] || '' : ''}
                      templateValue={templateCtv}
                      cpotTriggered={cpotTriggered}
                      targetValueDoseSuffixEnabled={targetValueDoseSuffixEnabled}
                      updateField={(value: string) => {
                        // Put the value into the array
                        const ctvArray = Array.isArray(state[siteIndex].CTV) ? [...state[siteIndex].CTV] : [];
                        ctvArray[tvIndex] = removeUnicodeQuotes(value);
                        const CTVOffTemplate = !cpotTriggered && !!templateCtv && templateCtv !== value;
                        // Set the state with the new array and the Off Template value
                        const newState = [...state];
                        newState[siteIndex].CTV = ctvArray;
                        newState[siteIndex].CTVOffTemplate = CTVOffTemplate;
                        setState(newState);
                        updateSiteVoluming({
                          variables: {
                            id: site.CTVId,
                            siteId: site.id,
                            fieldName: 'CTV',
                            value: JSON.stringify(ctvArray),
                          },
                        });
                      }}
                    />
                    <TargetValueField
                      index={tvIndex}
                      prefix="P"
                      suffix={doseValue}
                      initialValue={Array.isArray(site.PTV) ? site.PTV[tvIndex] || '' : ''}
                      templateValue={templatePtv}
                      cpotTriggered={cpotTriggered}
                      targetValueDoseSuffixEnabled={targetValueDoseSuffixEnabled}
                      updateField={(value: string) => {
                        // Put the value into the array
                        const ptvArray = Array.isArray(state[siteIndex].PTV) ? [...state[siteIndex].PTV] : [];
                        ptvArray[tvIndex] = removeUnicodeQuotes(value);
                        const PTVOffTemplate = !cpotTriggered && !!templatePtv && templatePtv !== value;
                        // Set the state with the new array and the Off Template value
                        const newState = [...state];
                        newState[siteIndex].PTV = ptvArray;
                        newState[siteIndex].PTVOffTemplate = PTVOffTemplate;
                        setState(newState);
                        updateSiteVoluming({
                          variables: {
                            id: site.PTVId,
                            siteId: site.id,
                            fieldName: 'PTV',
                            value: JSON.stringify(ptvArray),
                          },
                        });
                      }}
                    />
                  </Stack>
                );
              })}
          </div>
        );
      })}
    </div>
  );
};

export default SiteTargetValues;
